import React, { useState } from "react";
import { Download, ListPlus, FileText, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const ACTION_RE = /```action\s*([\s\S]*?)(```|$)/;

export const splitAction = (text) => {
  const m = text.match(ACTION_RE);
  if (!m) return { body: text, action: null };
  let action = null;
  try { action = JSON.parse(m[1].trim()); } catch { /* bloc incomplet pendant la diffusion */ }
  return { body: text.replace(ACTION_RE, "").trim(), action };
};

const inline = (line) => line.split(/(\*\*[^*]+\*\*)/g).map((p, k) => (p.startsWith("**") && p.endsWith("**") ? <strong key={k}>{p.slice(2, -2)}</strong> : p));
const cellsOf = (l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
const isSep = (l) => cellsOf(l).every((c) => /^[-: ]+$/.test(c));

export function Rich({ text, dark }) {
  const lines = text.split("\n");
  const out = [];
  for (let i = 0; i < lines.length; i += 1) {
    const l = lines[i];
    if (/^\s*\|.*\|\s*$/.test(l)) {
      const rows = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { if (!isSep(lines[i])) rows.push(cellsOf(lines[i])); i += 1; }
      i -= 1;
      out.push(
        <div key={i} className="overflow-x-auto my-2">
          <table className={`text-xs border-collapse min-w-full ${dark ? "text-white/90" : "text-brand-greenDeep"}`}>
            <tbody>{rows.map((r, ri) => <tr key={ri} className={ri === 0 ? "font-semibold" : ""}>{r.map((c, ci) => <td key={ci} className={`px-2 py-1 border ${dark ? "border-white/20" : "border-brand-soft"}`}>{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
    } else if (!l.trim()) out.push(<div key={i} className="h-2" />);
    else if (/^#{1,6}\s/.test(l)) out.push(<p key={i} className="font-semibold mt-1">{inline(l.replace(/^#{1,6}\s*/, ""))}</p>);
    else out.push(<p key={i}>{inline(l.replace(/^\s*[-*]\s+/, "• "))}</p>);
  }
  return <>{out}</>;
}

export async function downloadExport(format, title, content) {
  try {
    const r = await api.post("/ai/export", { format, title, content }, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${title.toLowerCase().replace(/[^a-z0-9]+/gi, "-").slice(0, 50) || "reponse-ia"}.${format}`;
    a.click(); URL.revokeObjectURL(url);
  } catch { toast.error("Export impossible"); }
}

export function ExportBar({ content, title, testid }) {
  const hasTable = /^\s*\|.*\|\s*$/m.test(content);
  const fmts = [...(hasTable ? [["csv", "CSV"], ["xlsx", "Excel"]] : []), ["pdf", "PDF"], ["docx", "Word"]];
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={testid}>
      <Download className="w-3.5 h-3.5 text-brand-green/40" />
      {fmts.map(([f, l]) => (
        <button key={f} onClick={() => downloadExport(f, title, content)} className="text-[11px] px-2 py-0.5 rounded-full border border-brand-soft text-brand-green/70 hover:bg-brand-softer" data-testid={`${testid}-${f}`}>{l}</button>
      ))}
    </div>
  );
}

export function ActionCard({ action, testid }) {
  const [a, setA] = useState(action);
  const [busy, setBusy] = useState(false);
  const doneKey = `ai_action_done:${JSON.stringify(action).slice(0, 200)}`;
  const [done, setDoneState] = useState(() => !!localStorage.getItem(doneKey));
  const setDone = (v) => { setDoneState(v); if (v) localStorage.setItem(doneKey, "1"); };
  const set = (k, v) => setA((x) => ({ ...x, [k]: v }));
  const inp = "w-full px-3 py-2 rounded-xl border border-brand-soft text-sm outline-none focus:border-brand-lime bg-white";
  if (a.type === "task") {
    const create = async () => {
      setBusy(true);
      try { await api.post("/tasks", { title: a.title, due: a.due || "", assignee: a.assignee || "", status: "À faire" }); setDone(true); toast.success("Tâche créée dans « Tâches à venir »"); }
      catch { toast.error("Création impossible"); } finally { setBusy(false); }
    };
    return (
      <div className="mt-3 rounded-2xl border border-brand-lime bg-brand-lime/10 p-4 space-y-2" data-testid={testid}>
        <p className="text-xs font-semibold text-brand-greenDeep flex items-center gap-1.5"><ListPlus className="w-4 h-4" /> Tâche proposée</p>
        <input value={a.title || ""} onChange={(e) => set("title", e.target.value)} className={inp} data-testid={`${testid}-title`} />
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={a.due || ""} onChange={(e) => set("due", e.target.value)} className={inp} data-testid={`${testid}-due`} />
          <input value={a.assignee || ""} onChange={(e) => set("assignee", e.target.value)} placeholder="Personne en charge" className={inp} data-testid={`${testid}-assignee`} />
        </div>
        <button onClick={create} disabled={busy || done || !a.title} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-greenDeep text-white text-xs font-semibold disabled:opacity-60" data-testid={`${testid}-confirm`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {done ? "Tâche créée" : "Créer la tâche"}
        </button>
      </div>
    );
  }
  if (a.type === "letter") {
    const content = `**Destinataire :** ${a.recipient || ""}\n\n**Objet :** ${a.title || ""}\n\n${a.body || ""}`;
    return (
      <div className="mt-3 rounded-2xl border border-brand-lime bg-brand-lime/10 p-4 space-y-2" data-testid={testid}>
        <p className="text-xs font-semibold text-brand-greenDeep flex items-center gap-1.5"><FileText className="w-4 h-4" /> Courrier préparé — relisez puis téléchargez</p>
        <input value={a.recipient || ""} onChange={(e) => set("recipient", e.target.value)} placeholder="Destinataire" className={inp} data-testid={`${testid}-recipient`} />
        <input value={a.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="Objet" className={inp} data-testid={`${testid}-subject`} />
        <textarea value={a.body || ""} onChange={(e) => set("body", e.target.value)} rows={8} className={inp} data-testid={`${testid}-body`} />
        <div className="flex gap-2">
          <button onClick={() => downloadExport("docx", a.title || "courrier", content)} className="px-4 py-2 rounded-full bg-brand-greenDeep text-white text-xs font-semibold" data-testid={`${testid}-docx`}>Télécharger Word</button>
          <button onClick={() => downloadExport("pdf", a.title || "courrier", content)} className="px-4 py-2 rounded-full border border-brand-greenDeep text-brand-greenDeep text-xs font-semibold" data-testid={`${testid}-pdf`}>Télécharger PDF</button>
        </div>
      </div>
    );
  }
  return null;
}
